import React, { useState, useMemo } from "react";
import { Handshake, ArrowRight, Search, HelpCircle, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { siteData } from "../data.js";
import SEO from "../components/SEO.jsx";
import { useCmsCollection } from "../lib/cms.js";

export default function Samarbejde() {
  const [searchQuery, setSearchQuery] = useState("");

  const { data: rawPartners } = useCmsCollection('samarbejdspartnere', siteData.cooperationPartners || []);

  // Sort all partners alphabetically by name (Danish sorting)
  const partners = useMemo(() => {
    const list = rawPartners.map((item, idx) => {
      const d = item.data || item;
      return {
        id: d.id || item.id || `partner-${idx}`,
        name: d.navn || d.name,
        description: d.beskrivelse || d.description,
        icon: d.ikon || d.icon || "fa-solid fa-handshake",
      };
    });
    return list.sort((a, b) => a.name.localeCompare(b.name, "da"));
  }, [rawPartners]);

  // Filter partners based on search query
  const filteredPartners = useMemo(() => {
    if (!searchQuery.trim()) return partners;
    const query = searchQuery.toLowerCase();
    return partners.filter((partner) => {
      return (
        partner.name.toLowerCase().includes(query) ||
        partner.description.toLowerCase().includes(query)
      );
    });
  }, [partners, searchQuery]);

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-16">
      <SEO
        title="Samarbejdspartnere | Stærke partnerskaber for frontpersonalet"
        description="Se hvem Tak For Din Indsats samarbejder med. Sammen med fonde, organisationer og behandlere skaber vi trygge rammer og støtte til Danmarks frontpersonale."
      />
      {/* Hero Section */}
      <div className="relative bg-slate-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-red-900/40 via-slate-900 to-slate-900" />
        <div className="absolute inset-0 opacity-10 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:14px_24px]" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="inline-flex items-center justify-center p-3 bg-red-500/10 rounded-2xl text-red-400 mb-6 border border-red-500/20 backdrop-blur-sm">
            <Handshake className="h-8 w-8" />
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-4">
            Samarbejde
          </h1>
          <p className="text-lg md:text-xl text-slate-300 max-w-3xl lg:max-w-4xl mx-auto font-light leading-relaxed">
            Tak For Din Indsats samarbejder med en bred vifte af organisationer, faglige forbund og foreninger<br className="hidden sm:inline" /> for at styrke sikkerhedsnettet under Danmarks frontpersonale.
          </p>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20">
        
        {/* Search Bar & Counter */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-xl shadow-slate-100/40 p-5 mb-12">
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
            
            {/* Search Input */}
            <div className="relative w-full sm:max-w-sm">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Søg i samarbejdspartnere..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all text-sm"
              />
            </div>

            {/* Alphabetical indicator / count */}
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 bg-slate-50 px-3.5 py-2 rounded-xl border border-slate-200/60 self-stretch sm:self-auto justify-center">
              <Users className="w-3.5 h-3.5 text-red-500" />
              <span>{filteredPartners.length} {filteredPartners.length === 1 ? "samarbejdspartner" : "samarbejdspartnere"} (A–Å)</span>
            </div>

          </div>
        </div>

        {/* Partners Grid */}
        {filteredPartners.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {filteredPartners.map((partner) => (
              <div
                key={partner.id}
                className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm hover:shadow-md hover:border-slate-200 transition-all flex flex-col justify-between group"
              >
                <div>
                  {/* Icon */}
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center text-red-600 group-hover:bg-red-600 group-hover:text-white transition-all duration-300 shadow-sm">
                      <i className={`${partner.icon} text-xl`} />
                    </div>
                  </div>

                  {/* Partner Name */}
                  <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-red-600 transition-colors">
                    {partner.name}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-slate-600 leading-relaxed mb-6">
                    {partner.description}
                  </p>
                </div>

                {/* Bottom Border / Accent Line */}
                <div className="h-1 w-0 bg-red-600 rounded-full group-hover:w-full transition-all duration-300" />
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center max-w-md mx-auto mb-16 shadow-sm">
            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-400">
              <HelpCircle className="h-6 w-6" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-1">
              Ingen resultater fundet
            </h3>
            <p className="text-sm text-slate-500 mb-6">
              Vi kunne ikke finde nogen samarbejdspartnere, der matcher din søgning "{searchQuery}".
            </p>
            <button
              onClick={() => setSearchQuery("")}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-xs transition-colors"
            >
              Nulstil søgning
            </button>
          </div>
        )}

        {/* Cooperation CTA Section */}
        <div className="bg-slate-900 rounded-3xl text-white p-8 md:p-12 relative overflow-hidden shadow-xl">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-red-900/30 via-slate-900 to-slate-900 opacity-80" />
          
          <div className="relative z-10 max-w-3xl">
            <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-500/20 text-red-400 border border-red-500/30">
              Bliv samarbejdspartner
            </span>
            <h2 className="text-2xl md:text-3xl font-black mt-4 mb-4 tracking-tight">
              Vil din organisation samarbejde med os?
            </h2>
            <p className="text-slate-300 text-sm md:text-base leading-relaxed mb-8 max-w-2xl">
              Vi søger altid nye stærke alliancer, der deler vores vision om et stærkt, statsgaranteret sikkerhedsnet under Danmarks frontpersonale. Kontakt os i dag for at høre mere om, hvordan vi kan løfte opgaven i fællesskab.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center px-6 py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-colors shadow-lg shadow-red-600/20 text-sm"
              >
                Kontakt os for samarbejde <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
              <Link
                to="/om-os"
                className="inline-flex items-center justify-center px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-colors text-sm backdrop-blur-sm border border-white/10"
              >
                Læs mere om vores mission
              </Link>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
